<template>
	<BaseLayout background-color-class="bg-color-white" :footer-full="true">
		<div class="container pb-300">
			<div class="grid-md gutters-300-md">
				<div class="col-8-md col-offset-2-md">
					<BasePlaceHolderInline width="4em" :placehold="loading">
						<HelpCenterHeader
							v-if="article"
							:category="article.articleCategory"
							:section="article.articleSection"
							class="mb-50"
						/>
					</BasePlaceHolderInline>
					<article>
						<BasePlaceHolder :class="{ 'mt-200': loading }" :line-count="1" :placehold="loading">
							<h1 class="heading-2 mb-100">{{ article.title }}</h1>
						</BasePlaceHolder>
						<BasePlaceHolder
							:class="{ 'mt-100': loading }"
							:section-count="2"
							:line-count="8"
							:placehold="loading"
						>
							<div class="article-body wysiwyg-generated-content" v-html="article.body"></div>
						</BasePlaceHolder>
					</article>
					<section v-if="!loading && userZendeskId">
						<HelpCenterVotingSection
							data-test="help-center-voting-section"
							:article-id="article.id"
							:zendesk-id="userZendeskId"
							class="mt-150"
						/>
					</section>
					<BasePlaceHolder :class="{ 'mt-150': loading }" :line-count="4" :placehold="loading">
						<section class="related-articles mt-150 pt-150 bt">
							<h3 class="heading-5"> Related Articles </h3>
							<ul class="ul">
								<li
									v-for="relatedArticle in article.relatedArticles"
									:key="relatedArticle.id"
									class="mt-100 line-height-100"
								>
									<BaseLink
										:link="{
											router: 'help-center-article',
											params: { articleId: relatedArticle.id }
										}"
										class="headline-link"
									>
										{{ relatedArticle.title }}
									</BaseLink>
								</li>
							</ul>
						</section>
					</BasePlaceHolder>
				</div>
			</div>
		</div>
		<HelpCenterContactBanner class="bottom-banner" :article-title="article.title" />
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, onUnmounted, Ref, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getHelpCenterArticle } from '@api/help-center';
import { HelpCenterArticle } from 'types/help-center';
import HelpCenterContactBanner from '@components/help-center/help-center-contact-banner.vue';
import HelpCenterHeader from '@components/help-center/help-center-header.vue';
import HelpCenterVotingSection from '@components/help-center/help-center-voting-section.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import { useMixpanel } from '@utils/composables/use-mixpanel';
import { useUserStore } from '@stores/user';

const userStore = useUserStore();

const route = useRoute();
const router = useRouter();

const appStore = useAppStore();

const headControl = useHead({ script: [`window.prerenderReady = false;`] });

const loading = ref(true);

const article: Ref<HelpCenterArticle> = ref({
	id: '',
	title: '',
	body: '',
	snippet: '',
	articleSection: null,
	articleCategory: null,
	relatedArticles: []
});

const articleId = computed((): string => {
	return route.params.articleId as string;
});

const userZendeskId = computed((): string | undefined => {
	return userStore.user?.zendeskId;
});

const { trackMixpanelView } = useMixpanel();
async function setup(): Promise<void> {
	try {
		article.value = await getHelpCenterArticle(articleId.value);
		loading.value = false;
		headControl?.patch({
			title: article.value.title ?? 'Help Center',
			meta: [
				{ property: 'og:title', content: article.value.title ?? 'Help Center' },
				{ name: 'description', content: article.value.snippet ?? article.value.title ?? '' },
				{ property: 'og:description', content: article.value.snippet ?? article.value.title ?? '' }
			],
			script: [`window.prerenderReady = true;`]
		});
		trackMixpanelView('Page', { 'View Title': article.value.title });
	} catch {
		appStore.addToast({
			type: 'info',
			message: 'No matching article was found. Search below for available help center articles.'
		});
		await router.push({ name: 'help-center-landing', query: { notFound: 'true' } });
	}
}

setup();

onUnmounted(() => {
	headControl?.dispose();
});
</script>
<style lang="scss">
@use '../../styles/constants/colors.scss' as *;

.bottom-banner {
	margin-bottom: -48px;
}

.article-body table {
	width: 100%;
}

.article-body table > * > * > td,
.article-body table > * > * > th {
	padding-top: 0.75em;
	padding-bottom: 0.75em;
	border-top: 1px solid gray(30);
	line-height: 1.5em;
}

.article-body table > * > * > * + td,
.article-body table > * > * > * + th {
	padding-left: 1em;
}

.article-body table *:first-child > *:first-child > * {
	border-top: none;
}

.module.module--is-clickable {
	transition: 0.25s;
}

.module.module--is-clickable:hover {
	transform: translateY(-2px);
	box-shadow:
		0 0 0 1px rgba(0, 0, 0, 0.02),
		0 2px 24px 0 rgba(0, 0, 0, 0.16);
}

.module.module--is-clickable:hover:active {
	box-shadow:
		0 0 0 1px rgba(0, 0, 0, 0.02),
		0 2px 8px rgba(0, 0, 0, 0.08);
	transform: none;
}

.module.module--is-clickable:hover .button {
	background-color: token('brand-primary');
	background-image: none;
	border-color: transparent;
	color: #fff;
}
</style>
